
if (!window.PlanBuilderPricing) {
  // The value most in-stock cards agree on; ties go to the smaller. `valueOf`
  // returns a card's vote, or anything not > 0 to abstain.
  const mostCommon = (cards, valueOf) => {
    const counts = new Map();
    (cards || []).forEach((card) => {
      const value = valueOf(card);
      if (card.soldOut || !(value > 0)) return;
      counts.set(value, (counts.get(value) || 0) + 1);
    });
    let best = 0;
    let bestCount = 0;
    counts.forEach((count, value) => {
      if (count > bestCount || (count === bestCount && value < best)) {
        best = value;
        bestCount = count;
      }
    });
    return best;
  };

  // Fallback worth of one empty slot per category, in cents, used only when a
  // section has no in-stock card to read a price off (see slotFromCards). Set to
  // the typical catalogue price so even the fallback lands near the real total.
  const RATES = { breakfast: 600, meals: 900, snacks: 300 };

  // The plan's per-meal share of the delivery fee, in cents, which only the
  // fallback rate needs (a card's own price already carries it). The fee lives
  // inside the meals' plan-tier variants, so it is read off the cards (tier
  // price - Standard price) and never configured: the catalogue stays its only
  // source. It is the
  // same for every dish in a plan; the most common wins so one mispriced dish
  // can't move every slot. Sections with no tier variants (breakfasts, snacks)
  // come out at 0.
  // `cards` is [{ price, standardPrice, soldOut }]; standardPrice is absent/0 on
  // cards that have no tier variant.
  const upliftCents = (cards) =>
    mostCommon(cards, (card) => (card.standardPrice > 0 ? card.price - card.standardPrice : 0));

  // What one EMPTY slot is worth, read off the section's own cards: the price
  // most in-stock cards sell at. `price` is the variant the card sells, so under
  // a plan tier it already carries the delivery share. The goal is a starting
  // figure that matches the real total once the plan is filled: fill it with
  // the section's typical dish and the total does not move at all, and the
  // cards are exactly what this plan, filter and stock allow, so each plan's
  // own collection prices its own slots. Ties go to the cheaper price.
  // `cards` is [{ price, soldOut }].
  const slotFromCards = (cards) => mostCommon(cards, (card) => card.price);

  // What one EMPTY slot of a section is worth: the price read off its cards,
  // or, with no card to read, its category's fallback rate plus the delivery
  // share. The one definition both figures below price from.
  const slotCents = (section) =>
    section.slot > 0 ? section.slot : (RATES[section.key] || 0) + (section.uplift || 0);

  // The starting figure, before anything is picked: every slot at slotCents.
  // Uncapped sections (extras) have no slots to count.
  // `sections` is [{ key, cap, slot, uplift, selections: [{ variantPrice, qty }] }].
  const estimateCents = (sections) =>
    (sections || []).reduce((cents, s) => cents + Math.max(s.cap || 0, 0) * slotCents(s), 0);

  // What the picked dishes actually cost: each at its own price, which is what
  // checkout charges. No floor: a cheaper dish is a cheaper plan.
  const realCents = (sections) =>
    (sections || []).reduce(
      (cents, s) => cents + (s.selections || []).reduce((c, x) => c + (x.variantPrice || 0) * x.qty, 0),
      0,
    );

  // The plan price the overview shows. It starts as the estimate and follows the
  // customer's choices: every picked dish counts at its own price and only the
  // slots still empty stay on slotCents. So it moves with each pick, and
  // with every slot filled nothing estimated is left: it is exactly the picked
  // dishes, which is what checkout charges. Swaps then move it up or down.
  // Uncapped sections (extras) add only what is picked.
  const planCents = (sections) =>
    (sections || []).reduce((cents, s) => {
      const picked = (s.selections || []).reduce((n, x) => n + x.qty, 0);
      const empty = Math.max((s.cap || 0) - picked, 0);
      return cents + realCents([s]) + empty * slotCents(s);
    }, 0);

  window.PlanBuilderPricing = { RATES, upliftCents, slotFromCards, estimateCents, realCents, planCents };
}
